<template>
  <div id="analytics-dashboard">
    <div class="top-container">
      <section class="filter-result">
        <span>{{ filtersResult }}</span>
      </section>
      <CollapseController
        :isCollapsed="!isPresentingExpandedFilters"
        :showCalendar="showCalendar"
        :buildingScore="false"
        @presentCalendar="() => (isPresentingCalendar = true)"
        @toggleCollapse="() => (isPresentingExpandedFilters = !isPresentingExpandedFilters)"
      />
    </div>
    <div class="horizontal-divider"></div>
    <div class="scrollable-container" :class="computedClass">
      <FeatureComponent
        feature="home/*/*/*/filters"
        :information="current.data"
        :isCollapsed="!isPresentingExpandedFilters"
        @resetForm="resetForm"
        @submitForm="submitForm"
      />
      <UnderlineTabGroup
        v-if="buttons && buttons.length > 1"
        :buttons="buttons"
        :size="'small'"
        :style="'margin-top: 16px;'"
        :selected-name="route.path.split('/').pop()"
      />
      <FeatureComponent
        :isLoading="isLoading"
        :isMissingData="isMissingData"
        :isMissingFilter="!filters?.submittedMeta.hasGeographicalFilter"
        feature="home/*/*/*/data/*"
      />
    </div>

    <teleport to="#home">
      <DashboardCalendar
        v-if="isPresentingCalendar"
        @calendarClose="() => (isPresentingCalendar = false)"
        @calendarSubmit="submitCalendarForm"
      />
    </teleport>
  </div>
</template>
<script lang="ts" setup>
import DashboardCalendar from '../components/DashboardCalendar.vue'
import CollapseController from '../components/CollapseController/CollapseController.vue'
import UnderlineTabGroup from '@/design-system/molecules/UnderlineTabGroup.vue'
import { ref, watch, inject, computed } from 'vue'
import { useRoute } from 'vue-router'
import { sharedDataKey } from '../../types'
import { useI18n } from 'vue-i18n'
import useFeatures, { useFeature } from '@/pinia/features'
import { useResultReportNew } from '../useResultReport'
import FeatureComponent from '@/components/FeatureComponent.vue'
import { FeatureType, useCurrentFeatureFilters } from '@/composables/useFilters'
import { useStore } from 'vuex'
import { useTransferState } from '../useTransferState'
import { useInformation } from '@/pinia/information'
import { storeToRefs } from 'pinia'
import { useMapboxFeatureState } from '@/pinia/mapboxFeatureState'

const { locale } = useI18n()

type Props = {
  title: string
  showCalendar?: boolean
}

withDefaults(defineProps<Props>(), {
  showCalendar: true
})

type Emits = {
  (e: 'toggleFullscreen'): void
  (e: 'closeDashboard'): void
}

defineEmits<Emits>()

const filters = useCurrentFeatureFilters()
const features = useFeatures()
const { replaceFeature, setFeature } = features
const { feature: sections } = useFeature('home/*/*/*/data')
const { feature: currentSection } = useFeature('home/*/*/*/data/*')
const { resetRecords } = useMapboxFeatureState()
const route = useRoute()
const information = useInformation()
const { submitted, current } = storeToRefs(information)
const data = computed(() => submitted.value?.data)
const isLoading = computed(() => !submitted.value?.data || (submitted.value?.isLoading ?? true))

const sharedData = inject(sharedDataKey)

const buttons = computed(() =>
  sections.value
    ? sections.value.children?.map((section) => ({
        text: section.title[locale.value],
        name: section.name,
        onClick: () => {
          if (section.name !== currentSection.value?.name) {
            resetRecords()
            replaceFeature(section.name)
            information.fetch()
          }
        }
      }))
    : undefined
)
const store = useStore()
useTransferState(store)

const isPresentingExpandedFilters = ref(false)
const isPresentingCalendar = ref(false)

const { report } = useResultReportNew(data)
watch(
  data,
  () => {
    if (data.value && 'numberOf' in data.value) {
      filters.value?.updateInformationResponse({
        buildings: data.value.numberOf?.CONSTRUCTION,
        rooms: 'SPACE' in data.value.numberOf ? data.value.numberOf.SPACE : undefined
      })
    }
  },
  { immediate: true }
)

const isMissingData = computed(() => {
  return (
    Object.keys(data.value && 'numberOf' in data.value ? data.value.numberOf : {}).every(
      (key) => data.value && 'numberOf' in data.value && data.value.numberOf[key] === 0
    ) &&
    data.value &&
    'fields' in data.value &&
    (data.value.fields as any[] | undefined)?.length === 0
  )
})

const isProjectAnalysis = computed(() => {
  return (
    filters.value?.current.type === FeatureType.ProjectAnalysisExternal ||
    filters.value?.current.type === FeatureType.ProjectAnalysisInternal
  )
})

const filtersResult = computed(() => {
  if (isProjectAnalysis.value && !filters.value?.submittedGeographicalFilters.complex) {
    if (locale.value === 'en') {
      return 'Results based on selection of - buildings'
    } else {
      return 'Resultat baserat på urval av - byggnader'
    }
  }

  if (filters.value?.submittedGeographicalFilters.room) {
    if (locale.value === 'en') {
      return 'Results based on selection of 1 building and 1 room'
    } else {
      return 'Resultat baserat på urval av 1 byggnad och 1 rum'
    }
  } else {
    return report.value
  }
})

watch(
  sections,
  () => {
    if (!sections.value?.parent) {
      return
    }
    const featureName = sections.value?.name
    const firstChildFeature = sections.value?.children?.[0]
    const currentSectionName = currentSection.value?.name
    if (featureName && firstChildFeature && !currentSectionName) {
      setFeature(firstChildFeature)
    }
  },
  { immediate: true }
)

const resetForm = () => {
  filters.value?.reset()
}
const submitForm = () => {
  filters.value?.submit()
  isPresentingExpandedFilters.value = false
}
const submitCalendarForm = () => {
  filters.value?.submit()
  isPresentingCalendar.value = false
}
const computedClass = computed(() => {
  return {
    fullscreen: sharedData?.fullscreen
  }
})

watch(
  sections,
  (feature) => {
    if (feature) {
      filters.value?.submit()
    }
  },
  { immediate: true }
)
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

#analytics-dashboard {
  position: relative;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  background-color: #fff;
  border-radius: variables.$border-radius-small;
  &.fullscreen {
    position: fixed;
    width: 100%;
    top: calc(#{variables.$layout-top-bar-height});
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0;
    background-color: #fff;
  }
}
.top-container {
  flex-shrink: 0;
  padding: 0 24px 24px 24px;
  padding-bottom: 0;
  .dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    h4 {
      @include font-mixins.desktop-bold-h4;
    }
    button {
      border: none;
      border-radius: variables.$border-radius-small;
      display: flex;
      justify-content: center;
      align-items: center;
      background: none;
      padding: variables.$padding-medium;
      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
  }
}
.scrollable-container {
  padding: 16px variables.$padding-main-body 24px variables.$padding-main-body;
  padding-top: 0px;
  overflow-y: scroll;
  scroll-behavior: smooth;
  scrollbar-width: none;
  background-color: #f9f9f9;
  height: 100%;
  &::-webkit-scrollbar {
    display: none;
  }
}
.horizontal-divider {
  height: 1px;
  width: 100%;
  background-color: #e0e0e0;
}

.filter-result {
  margin-top: 16px;
  span {
    @include font-mixins.desktop-regular-label;
    color: variables.$color-greyscale-content;
    display: flex;
    flex-direction: row;
    gap: variables.$gap-xlarge;
  }
}
</style>
